import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Package, Calendar, Building2,
  FileText, DollarSign, TrendingUp, MapPin, CheckCircle2
} from 'lucide-react';
import { incomingService } from '../../../../services/erpApi';
import { useToast } from '../../../../hooks/useToast';
import { Toast } from '../../../../components';
import './IncomingViewPage.css';

interface ViewItem {
  id: number;
  product_id: number;
  product_name: string;
  sku: string;
  xml_description?: string;
  supplier_code?: string;
  xml_barcode?: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  xml_unit: string;
  location_code?: string;
  cost_price: number;
  old_price: number;
  current_price: number;
  markup: number;
  margin: number;
}

interface InvoiceEntry {
  id: number;
  number: string;
  series?: string;
  model?: string;
  access_key?: string;
  supplier_id: number;
  supplier_name?: string;
  issue_date: string;
  entry_date: string;
  total_amount: number;
  status: string;
  observation?: string;
  created_at: string;
  validation_approved_by?: number;
  validation_approved_at?: string;
  inspection_processed_at?: string;
  inspection_processed_by?: number;
  launched_at?: string;
  launched_by?: number;
  pricing_processed_at?: string;
  pricing_processed_by?: number;
}

interface ViewData {
  invoiceEntry: InvoiceEntry;
  items: ViewItem[];
}

const IncomingViewPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [viewData, setViewData] = useState<ViewData | null>(null);

  useEffect(() => {
    if (id) {
      loadViewData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadViewData = async () => {
    try {
      setLoading(true);
      const response = await incomingService.getView(parseInt(id!));
      if (response.success) {
        setViewData(response.data);
      } else {
        showToast('Nota fiscal não encontrada', 'error');
        navigate('/erp/stock/incoming');
      }
    } catch (error: any) {
      console.error('Erro ao carregar dados:', error);
      showToast('Erro ao carregar dados da nota fiscal', 'error');
      navigate('/erp/stock/incoming');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/erp/stock/incoming');
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (loading) {
    return (
      <div className="incoming-view-loading">
        <Loader2 size={32} className="incoming-view-spinner" />
        <p>Carregando dados da nota fiscal...</p>
      </div>
    );
  }

  if (!viewData) {
    return null;
  }

  const { invoiceEntry, items } = viewData;
  const totalItems = items.length;
  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalCost = items.reduce((sum, item) => sum + item.total_price, 0);
  const totalOldPrice = items.reduce((sum, item) => sum + (item.old_price * item.quantity), 0);
  const totalCurrentPrice = items.reduce((sum, item) => sum + (item.current_price * item.quantity), 0);
  const totalPriceImpact = totalCurrentPrice - totalOldPrice;

  return (
    <div className="incoming-view">
      {/* Header */}
      <div className="incoming-view-header">
        <div className="incoming-view-header-left">
          <button 
            onClick={handleBack} 
            className="incoming-view-back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="incoming-view-title-row">
              <h1 className="incoming-view-title">
                Nota Fiscal #{invoiceEntry.number}
              </h1>
              <span className="incoming-view-badge">Concluída</span>
            </div>
            <p className="incoming-view-subtitle">
              Visualização Completa da Nota Fiscal
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="incoming-view-main-grid">
        {/* Left Column - Invoice Info and Items */}
        <div className="incoming-view-content">
          {/* Invoice Information Card */}
          <div className="incoming-view-card">
            <div className="incoming-view-card-header">
              <FileText size={20} />
              <h2>Informações da Nota Fiscal</h2>
            </div>
            <div className="incoming-view-card-content">
              <div className="incoming-view-info-grid">
                <div className="incoming-view-info-item">
                  <span className="incoming-view-info-label">Fornecedor:</span>
                  <span className="incoming-view-info-value">
                    <Building2 size={16} />
                    {invoiceEntry.supplier_name || 'N/A'}
                  </span>
                </div>
                <div className="incoming-view-info-item">
                  <span className="incoming-view-info-label">Número:</span>
                  <span className="incoming-view-info-value">{invoiceEntry.number}</span>
                </div>
                {invoiceEntry.series && (
                  <div className="incoming-view-info-item">
                    <span className="incoming-view-info-label">Série:</span>
                    <span className="incoming-view-info-value">{invoiceEntry.series}</span>
                  </div>
                )}
                {invoiceEntry.model && (
                  <div className="incoming-view-info-item">
                    <span className="incoming-view-info-label">Modelo:</span>
                    <span className="incoming-view-info-value">{invoiceEntry.model}</span>
                  </div>
                )}
                <div className="incoming-view-info-item">
                  <span className="incoming-view-info-label">Data de Emissão:</span>
                  <span className="incoming-view-info-value">
                    <Calendar size={16} />
                    {formatDate(invoiceEntry.issue_date)}
                  </span>
                </div>
                <div className="incoming-view-info-item">
                  <span className="incoming-view-info-label">Data de Entrada:</span>
                  <span className="incoming-view-info-value">
                    <Calendar size={16} />
                    {formatDate(invoiceEntry.entry_date)}
                  </span>
                </div>
                <div className="incoming-view-info-item">
                  <span className="incoming-view-info-label">Valor Total:</span>
                  <span className="incoming-view-info-value incoming-view-amount">
                    <DollarSign size={16} />
                    {formatCurrency(invoiceEntry.total_amount)}
                  </span>
                </div>
                {invoiceEntry.access_key && (
                  <div className="incoming-view-info-item incoming-view-info-full">
                    <span className="incoming-view-info-label">Chave de Acesso:</span>
                    <span className="incoming-view-info-value incoming-view-access-key">
                      {invoiceEntry.access_key}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Processing Timeline Card */}
          <div className="incoming-view-card">
            <div className="incoming-view-card-header">
              <CheckCircle2 size={20} />
              <h2>Histórico de Processamento</h2>
            </div>
            <div className="incoming-view-card-content">
              <div className="incoming-view-timeline">
                {invoiceEntry.validation_approved_at && (
                  <div className="incoming-view-timeline-item">
                    <div className="incoming-view-timeline-dot"></div>
                    <div className="incoming-view-timeline-content">
                      <p className="incoming-view-timeline-title">Validação Aprovada</p>
                      <p className="incoming-view-timeline-date">
                        <Calendar size={14} />
                        {formatDate(invoiceEntry.validation_approved_at)}
                      </p>
                    </div>
                  </div>
                )}
                {invoiceEntry.inspection_processed_at && (
                  <div className="incoming-view-timeline-item">
                    <div className="incoming-view-timeline-dot"></div>
                    <div className="incoming-view-timeline-content">
                      <p className="incoming-view-timeline-title">Conferência Concluída</p>
                      <p className="incoming-view-timeline-date">
                        <Calendar size={14} />
                        {formatDate(invoiceEntry.inspection_processed_at)}
                      </p>
                    </div>
                  </div>
                )}
                {invoiceEntry.launched_at && (
                  <div className="incoming-view-timeline-item">
                    <div className="incoming-view-timeline-dot"></div>
                    <div className="incoming-view-timeline-content">
                      <p className="incoming-view-timeline-title">Estoque Lançado</p>
                      <p className="incoming-view-timeline-date">
                        <Calendar size={14} />
                        {formatDate(invoiceEntry.launched_at)}
                      </p>
                    </div>
                  </div>
                )}
                {invoiceEntry.pricing_processed_at && (
                  <div className="incoming-view-timeline-item">
                    <div className="incoming-view-timeline-dot"></div>
                    <div className="incoming-view-timeline-content">
                      <p className="incoming-view-timeline-title">Precificação Concluída</p>
                      <p className="incoming-view-timeline-date">
                        <Calendar size={14} />
                        {formatDate(invoiceEntry.pricing_processed_at)}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="incoming-view-card">
            <div className="incoming-view-card-header">
              <Package size={20} />
              <h2>Itens da Nota Fiscal</h2>
            </div>
            <div className="incoming-view-card-content">
              <div className="incoming-view-table-wrapper">
                <table className="incoming-view-table">
                  <thead>
                    <tr>
                      <th>Produto</th>
                      <th className="text-center">Quantidade</th>
                      <th className="text-center">Preço Custo</th>
                      <th className="text-center">Preço Antigo</th>
                      <th className="text-center">Preço Atual</th>
                      <th className="text-center">Markup</th>
                      <th className="text-center">Localização</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div className="incoming-view-item-product">
                            <div className="incoming-view-item-product-icon">
                              <Package size={20} />
                            </div>
                            <div className="incoming-view-item-product-info">
                              <p className="incoming-view-item-product-name">
                                {item.xml_description || item.product_name}
                              </p>
                              <p className="incoming-view-item-product-sku">
                                SKU: {item.sku}
                              </p>
                              {item.supplier_code && (
                                <p className="incoming-view-item-product-code">
                                  Código: {item.supplier_code}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="incoming-view-item-qty">
                          {item.quantity.toFixed(2)} {item.xml_unit}
                        </td>
                        <td className="incoming-view-item-price">
                          {formatCurrency(item.cost_price)}
                        </td>
                        <td className="incoming-view-item-price">
                          {item.old_price > 0 ? formatCurrency(item.old_price) : 'N/A'}
                        </td>
                        <td className="incoming-view-item-price incoming-view-price-current">
                          {item.current_price > 0 ? formatCurrency(item.current_price) : 'N/A'}
                        </td>
                        <td className="incoming-view-item-markup">
                          {item.markup > 0 ? (
                            <span className="incoming-view-markup-positive">
                              <TrendingUp size={14} />
                              {item.markup.toFixed(1)}%
                            </span>
                          ) : (
                            <span className="incoming-view-markup-neutral">-</span>
                          )}
                        </td>
                        <td className="incoming-view-item-location">
                          {item.location_code ? (
                            <span className="incoming-view-location-badge">
                              <MapPin size={14} />
                              {item.location_code}
                            </span>
                          ) : (
                            <span className="incoming-view-location-empty">N/A</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Summary */}
        <div className="incoming-view-sidebar">
          <div className="incoming-view-summary-card">
            <h3 className="incoming-view-summary-title">
              <FileText size={16} />
              Resumo
            </h3>

            <div className="incoming-view-summary-content">
              <div className="incoming-view-summary-stats">
                <div className="incoming-view-summary-stat">
                  <p className="incoming-view-summary-stat-label">Total de Itens</p>
                  <p className="incoming-view-summary-stat-value">
                    {totalItems}
                  </p>
                </div>
                <div className="incoming-view-summary-stat">
                  <p className="incoming-view-summary-stat-label">Quantidade Total</p>
                  <p className="incoming-view-summary-stat-value">
                    {totalQuantity.toFixed(2)}
                  </p>
                </div>
              </div>

              <div className="incoming-view-summary-section">
                <h4 className="incoming-view-summary-section-title">Valores</h4>
                <div className="incoming-view-summary-item">
                  <span className="incoming-view-summary-item-label">Custo Total:</span>
                  <span className="incoming-view-summary-item-value">
                    {formatCurrency(totalCost)}
                  </span>
                </div>
                <div className="incoming-view-summary-item">
                  <span className="incoming-view-summary-item-label">Valor Antigo (Total):</span>
                  <span className="incoming-view-summary-item-value">
                    {formatCurrency(totalOldPrice)}
                  </span>
                </div>
                <div className="incoming-view-summary-item">
                  <span className="incoming-view-summary-item-label">Valor Atual (Total):</span>
                  <span className="incoming-view-summary-item-value incoming-view-summary-value-highlight">
                    {formatCurrency(totalCurrentPrice)}
                  </span>
                </div>
                {totalPriceImpact !== 0 && (
                  <div className="incoming-view-summary-item incoming-view-summary-impact">
                    <span className="incoming-view-summary-item-label">Impacto:</span>
                    <span className={`incoming-view-summary-item-value ${
                      totalPriceImpact > 0 ? 'incoming-view-impact-positive' : 'incoming-view-impact-negative'
                    }`}>
                      {totalPriceImpact > 0 ? '+' : ''}{formatCurrency(totalPriceImpact)}
                    </span>
                  </div>
                )}
              </div>

              {invoiceEntry.observation && (
                <div className="incoming-view-summary-section">
                  <h4 className="incoming-view-summary-section-title">Observações</h4>
                  <p className="incoming-view-summary-observation">
                    {invoiceEntry.observation}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
};

export default IncomingViewPage;
